import { ChangeLog, Response, apiAbortController } from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import { Balance } from '../../statements/types';
import api from '../../../api/api';
import {
  Account,
  AccountsStats,
  AccountsValidatorResponse,
  AccountFormValues,
  PspIntegration,
} from '../types';
import {
  ExternalAccount,
  ExternalAccountFormValues,
  ExternalAccountTopUpOption,
  ExternalAccountTopUpOptionFormValues,
  ExternalAccountWithIntegrationFormValues,
  LinkedClient,
  LinkedClientsFormValues,
  Organization,
  PspIntegrationOrganization,
  PspPartner,
} from '../../external/types';

export const accountsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAccountsValidators: build.query<AccountsValidatorResponse, void>({
      query: () => ({
        url: '/v1/accounts/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsList: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccount: build.query<
      { data: Account },
      { id: string; closingBalancesAt?: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}${
          data?.closingBalancesAt
            ? `?closingBalancesAt=${data?.closingBalancesAt}`
            : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsStats: build.query<{ data: AccountsStats[] }, void>({
      query: () => ({
        url: '/v1/accounts-stats',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsBalances: build.query<{ data: Balance[] }, string>({
      query: (query) => ({
        url: `/v1/balances/closing-balances${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountClosingBalances: build.query<{ data: Balance[] }, string>({
      query: (query) => ({
        url: `/v1/balances/closing-balances${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAccount: build.mutation<
      {
        data: Account;
      },
      AccountFormValues
    >({
      query: (body) => ({
        url: '/v1/accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockCredit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/block-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockCredit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/unblock-credit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    blockDebit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/block-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    unblockDebit: build.mutation<
      {
        data: Account;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/accounts/${id}/unblock-debit`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    closeAccount: build.mutation<
      {
        data: Account;
      },
      { body: { comment: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/close`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    openAccount: build.mutation<
      {
        data: Account;
      },
      { body: { comment: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/open`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAccount: build.mutation<
      {
        data: Account;
      },
      {
        id: string;
        closed?: boolean;
        revaluable?: boolean;
        description?: string;
        creditBlock?: boolean;
        debitBlock?: boolean;
      }
    >({
      query: (body) => ({
        url: `/v1/accounts/${body.id}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateOverdraftStatus: build.mutation<
      {
        data: Account;
      },
      {
        id: string;
        overdraftEnabled: boolean;
      }
    >({
      query: (body) => ({
        url: `/v1/accounts/${body.id}/overdraft`,
        method: 'PUT',
        body: { overdraftEnabled: body.overdraftEnabled },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateOverdraftBalanceValue: build.mutation<
      {
        data: Balance;
      },
      {
        id: string;
        balanceId: string;
        overdraftEnabled: boolean;
        overdraftValue: number;
      }
    >({
      query: (body) => ({
        url: `/v1/accounts/${body.id}/balances/${body.balanceId}/overdraft`,
        method: 'PUT',
        body: {
          overdraftValue: body.overdraftValue,
          overdraftEnabled: body.overdraftEnabled,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountBalances: build.query<Response<Balance>, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccountsList: build.query<Response<ExternalAccount>, string>({
      query: (query) => ({
        url: `/v1/psp-integrations-accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccount: build.query<{ data: ExternalAccount }, string>({
      query: (id) => ({
        url: `/v1/psp-integrations-accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExternalAccountCurrencies: build.mutation<
      { data: ExternalAccount },
      { id: string; currenciesIds: string[] }
    >({
      query: (body) => ({
        url: `/v1/psp-integrations-accounts/${body.id}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsForExternal: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsPspPartners: build.query<Response<PspPartner>, void>({
      query: () => ({
        url: '/v1/psp-partners?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createExternalAccount: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeAccountBalanceStatus: build.mutation<
      { data: Account },
      { id: string; body: { currencyId: string; active: boolean } }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExternalAccountStatus: build.mutation<
      { data: { count: number } },
      { ids: string[]; active: boolean }
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/status',
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportAccounts: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/accounts/export',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsCASP: build.query<Response<CASP>, void>({
      query: () => ({
        url: '/v1/casps?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrations: build.query<Response<PspIntegration>, void>({
      query: () => ({
        url: '/v1/psp-integrations',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createExternalAccountWithIntegration: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountWithIntegrationFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/generate-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccountTopUpOptions: build.query<
      Response<ExternalAccountTopUpOption>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts?pspIntegrationAccountId=${data.id}&all=true${data.query ? data.query : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExternalAccountChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsOrganizations: build.query<Response<Organization>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations?take=40${query ? `&${query}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsPspIntegrationsOrganizationsByName: build.query<
      { data: PspIntegrationOrganization[] },
      string
    >({
      query: (query) => ({
        url: `/v1/psp-integrations-organizations/search/external-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bindAccountsExternalOrganization: build.mutation<
      {
        data: LinkedClient;
      },
      LinkedClientsFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-organizations',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsLinkedClients: build.query<Response<LinkedClient>, string>({
      query: (query) => ({
        url: `/v1/psp-integrations-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deletePaymentAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/psp-integrations-payment-accounts/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      ExternalAccountTopUpOptionFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-payment-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      { id: string; body: ExternalAccountTopUpOptionFormValues }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetAccountsListQuery,
  useGetAccountsValidatorsQuery,
  useLazyGetAccountsStatsQuery,
  useCreateAccountMutation,
  useLazyGetAccountQuery,
  useLazyGetAccountBalancesQuery,
  useUpdateAccountMutation,
  useBlockCreditMutation,
  useBlockDebitMutation,
  useUnblockCreditMutation,
  useUnblockDebitMutation,
  useCloseAccountMutation,
  useOpenAccountMutation,
  useChangeAccountBalanceStatusMutation,
  useExportAccountsMutation,
  useLazyGetExternalAccountsListQuery,
  useCreateExternalAccountMutation,
  useLazyGetAccountsPspPartnersQuery,
  useLazyGetAccountsForExternalQuery,
  useUpdateExternalAccountStatusMutation,
  useUpdateOverdraftStatusMutation,
  useUpdateOverdraftBalanceValueMutation,
  useLazyGetAccountChangeLogsQuery,
  useLazyGetAccountsCASPQuery,
  useLazyGetExternalAccountQuery,
  useLazyGetAccountsBalancesQuery,
  useLazyGetAccountClosingBalancesQuery,
  useLazyGetPspIntegrationsQuery,
  useCreateExternalAccountWithIntegrationMutation,
  useLazyGetExternalAccountTopUpOptionsQuery,
  useLazyGetExternalAccountChangeLogsQuery,
  useUpdateExternalAccountCurrenciesMutation,
  useLazyGetAccountsOrganizationsQuery,
  useLazyGetAccountsPspIntegrationsOrganizationsByNameQuery,
  useLazyGetAccountsLinkedClientsQuery,
  useBindAccountsExternalOrganizationMutation,
  useDeletePaymentAccountsMutation,
  useCreatePaymentsAccountsMutation,
  useUpdatePaymentsAccountsMutation,
} = accountsListApi;

export const {
  endpoints: {
    getAccountsList,
    getAccount,
    getAccountsValidators,
    getAccountsStats,
    createAccount,
    getAccountBalances,
    updateAccount,
    blockCredit,
    blockDebit,
    unblockCredit,
    unblockDebit,
    closeAccount,
    openAccount,
    changeAccountBalanceStatus,
    exportAccounts,
    getExternalAccountsList,
    createExternalAccount,
    getAccountsPspPartners,
    getAccountsForExternal,
    updateExternalAccountStatus,
    updateOverdraftStatus,
    updateOverdraftBalanceValue,
    getAccountsCASP,
    getExternalAccount,
    getAccountsBalances,
    getAccountClosingBalances,
    getPspIntegrations,
    createExternalAccountWithIntegration,
    getExternalAccountTopUpOptions,
    getExternalAccountChangeLogs,
    updateExternalAccountCurrencies,
    getAccountsOrganizations,
    getAccountsLinkedClients,
    getAccountsPspIntegrationsOrganizationsByName,
    bindAccountsExternalOrganization,
    deletePaymentAccounts,
    createPaymentsAccounts,
    updatePaymentsAccounts,
  },
} = accountsListApi;
